import React from 'react';
import { Loader } from 'freemium-ui';
import { useTranslation } from 'react-i18next';
import {
  AccountInfoType,
  McrContactType,
  SurveyResponseType,
  SurveyType,
  SurveysResponseType,
} from '../../../../../shared/types';
import EmptyState from '../../../../../shared/components/EmptyState';
import Icon from '../../../../../shared/components/Icon';
import {
  useGetSurveyResponses,
  useGetSurveys,
} from '../../../../../shared/hooks';
import { Center } from '../../../../../shared/styles';
import { ResponseContainer } from '../styles';
import ResponseCard from '../ResponseCard';

type RecentResponsesProps = {
  contactData: McrContactType;
  accountInfo?: AccountInfoType;
};

type AvailableFilters = 'oldest' | 'latest';

const filters = {
  oldest: (a: SurveyResponseType, b: SurveyResponseType) =>
    new Date(a.created_at || '').valueOf() -
    new Date(b.created_at || '').valueOf(),
  latest: (a: SurveyResponseType, b: SurveyResponseType) =>
    new Date(b.created_at || '').valueOf() -
    new Date(a.created_at || '').valueOf(),
};

const RecentResponses = (props: RecentResponsesProps) => {
  const { t } = useTranslation();
  const { contactData, accountInfo } = props;

  const [surveyIds, setSurveyIds] = React.useState<string[]>([]);
  const [currentFilter, setFilter] = React.useState<AvailableFilters>('latest');

  const {
    data: responses,
    isFetching,
    status,
    refetch: refetchResponses,
  } = useGetSurveyResponses({
    ucrContactId: contactData.id || '',
    surveyIds,
    enabled: Boolean(contactData.id) && surveyIds.length > 0,
  });

  const { refetch: refetchSurveys, isFetching: isFetchingSurveys } =
    useGetSurveys({
      callback: (isSuccess: boolean, response: SurveysResponseType) => {
        if (isSuccess) {
          const { surveys = [] } = response;
          const allSurveyIds = surveys.map((survey: SurveyType) => {
            return survey.id || '';
          });
          setSurveyIds(allSurveyIds.filter((surveyId) => Boolean(surveyId)));
        }
      },
    });

  React.useEffect(() => {
    refetchSurveys();
  }, [contactData]);

  React.useEffect(() => {
    refetchResponses();
  }, [surveyIds]);

  if (isFetching || isFetchingSurveys || status === 'idle')
    return (
      <ResponseContainer>
        <Center>
          <Loader />
        </Center>
      </ResponseContainer>
    );

  return responses && responses?.length > 0 ? (
    <ResponseContainer>
      <div className="response-filters">
        <div className="filter" onClick={() => setFilter('oldest')}>
          <span>{t('oldest')}</span>
          <Icon glyph="arrow-up" width="8" height="8" />
        </div>
        <div className="filter" onClick={() => setFilter('latest')}>
          <span>{t('latest')}</span>
          <Icon glyph="arrow-down" width="8" height="8" />
        </div>
      </div>
      {responses?.sort(filters[currentFilter]).map((response, ind) => (
        <ResponseCard response={response} key={ind} accountInfo={accountInfo} />
      ))}
    </ResponseContainer>
  ) : (
    <ResponseContainer>
      <Center>
        <div className="text">
          <EmptyState
            imgSrc="empty-state-response.svg"
            title={`${t('empty_state.title.no_responses_here')}`}
            message={`${t('empty_state.message.no_responses_for_contact')}`}
          />
        </div>
      </Center>
    </ResponseContainer>
  );
};

export default RecentResponses;
